import React from 'react'
import { Icon } from './icons'
import { DataState, Dropdown } from './shared'
import { isApiError } from './api'
import type { ApiError, Capabilities, PropertiesState, PropertyEntry, Role } from './types'

interface PropertiesPageProps {
  serverId: string
  serverName: string
  role: Role
  capabilities: Capabilities
  onFetch: () => Promise<PropertiesState>
  onWrite: (body: {
    edits?: Record<string, string | number | boolean>
    raw?: string | null
  }) => Promise<PropertiesState | null>
}

function PropertyInput({
  entry,
  value,
  onChange,
  disabled,
}: {
  entry: PropertyEntry
  value: string
  onChange: (value: string) => void
  disabled: boolean
}) {
  if (entry.type === 'bool') {
    const checked = value === 'true'
    return (
      <label className="check" style={{ gap: 6 }}>
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={event => onChange(event.target.checked ? 'true' : 'false')}
        />
        <span className="dim text-xs">{checked ? 'true' : 'false'}</span>
      </label>
    )
  }
  if (entry.type === 'enum' && entry.enum && entry.enum.length > 0) {
    return (
      <Dropdown
        style={{ width: '100%' }}
        value={value}
        disabled={disabled}
        onChange={onChange}
        options={entry.enum.map(option => ({ value: option, label: option }))}
      />
    )
  }
  if (entry.type === 'int') {
    return (
      <input
        type="number"
        value={value}
        disabled={disabled}
        onChange={event => onChange(event.target.value)}
        style={{ width: '100%' }}
      />
    )
  }
  return (
    <input
      type="text"
      value={value}
      disabled={disabled}
      onChange={event => onChange(event.target.value)}
      style={{ width: '100%' }}
    />
  )
}

export function PropertiesPage({ serverId, serverName, role, capabilities, onFetch, onWrite }: PropertiesPageProps) {
  const [state, setState] = React.useState<PropertiesState | null>(null)
  const [values, setValues] = React.useState<Record<string, string>>({})
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<ApiError | null>(null)
  const [rawOpen, setRawOpen] = React.useState(false)
  const [rawText, setRawText] = React.useState('')
  const [rawConfirm, setRawConfirm] = React.useState(false)
  const [saving, setSaving] = React.useState(false)

  const reload = React.useCallback(async () => {
    setLoading(true)
    try {
      const data = await onFetch()
      setState(data)
      setValues(Object.fromEntries(data.keys.map(k => [k.key, k.value])))
      setRawText(data.raw)
      setError(null)
    } catch (err) {
      if (isApiError(err)) setError(err)
      else setError({ status: 0, message: 'Network error' })
    } finally {
      setLoading(false)
    }
  }, [onFetch])

  React.useEffect(() => { void reload() }, [reload, serverId])

  const canEdit = capabilities.canEditProperties && role !== 'player'
  const canRaw = capabilities.canRawEditProperties

  const dirty: string[] = React.useMemo(() => {
    if (!state) return []
    return state.keys.filter(k => k.editable && values[k.key] !== k.value).map(k => k.key)
  }, [state, values])

  const restartRequired = React.useMemo(() => {
    if (!state) return false
    return dirty.some(key => state.keys.find(k => k.key === key)?.requiresRestart)
  }, [state, dirty])

  async function handleSave() {
    if (dirty.length === 0) return
    const edits: Record<string, string | number | boolean> = {}
    for (const key of dirty) {
      const entry = state?.keys.find(k => k.key === key)
      if (!entry) continue
      if (entry.type === 'bool') edits[key] = values[key] === 'true'
      else if (entry.type === 'int') edits[key] = Number(values[key])
      else edits[key] = values[key]
    }
    setSaving(true)
    try {
      const res = await onWrite({ edits })
      if (res) {
        setState(res)
        setValues(Object.fromEntries(res.keys.map(k => [k.key, k.value])))
        setRawText(res.raw)
      }
    } finally {
      setSaving(false)
    }
  }

  async function handleRawSave() {
    if (!rawConfirm) return
    setSaving(true)
    try {
      const res = await onWrite({ raw: rawText })
      if (res) {
        setState(res)
        setValues(Object.fromEntries(res.keys.map(k => [k.key, k.value])))
        setRawText(res.raw)
        setRawOpen(false)
        setRawConfirm(false)
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="text-xs dim">Editing <span className="mono">{serverName}</span>/server.properties</div>
        <div className="ml-auto flex gap-2">
          {canRaw && !rawOpen && (
            <button className="btn sm ghost" onClick={() => setRawOpen(true)}>
              <Icon name="terminal" size={11} /> Advanced (raw)
            </button>
          )}
          <button className="btn sm ghost" onClick={() => void reload()}><Icon name="rotate-ccw" size={11} /> Reload</button>
          {canEdit && !rawOpen && (
            <button
              className="btn sm primary"
              disabled={saving || dirty.length === 0}
              onClick={() => void handleSave()}
            >
              <Icon name="check" size={11} /> Save {dirty.length > 0 ? `(${dirty.length})` : ''}
            </button>
          )}
        </div>
      </div>

      {restartRequired && (
        <div className="banner warn" style={{ marginBottom: 12 }}>
          <Icon name="alert-triangle" size={14} />
          <div>
            <div style={{ fontWeight: 500 }}>Restart required</div>
            <div className="text-xs" style={{ opacity: 0.85 }}>
              Changes to {dirty.filter(k => state?.keys.find(x => x.key === k)?.requiresRestart).join(', ')} take effect after the next restart.
            </div>
          </div>
        </div>
      )}

      <DataState loading={loading} error={error} onRetry={reload}>
        {rawOpen ? (
          <div className="card" style={{ padding: 14 }}>
            <div className="banner err" style={{ marginBottom: 10 }}>
              <Icon name="alert-triangle" size={14} />
              <div>
                <div style={{ fontWeight: 500 }}>Raw edit mode (owner only)</div>
                <div className="text-xs" style={{ opacity: 0.85 }}>
                  Writes the full file. Forbidden keys (rcon.password, server-port, rcon.port) are rejected by the VPS helper.
                </div>
              </div>
            </div>
            <textarea
              value={rawText}
              onChange={event => setRawText(event.target.value)}
              rows={22}
              spellCheck={false}
              className="mono"
              style={{ width: '100%', fontSize: 12 }}
            />
            <div className="flex gap-2 mt-3 justify-end">
              <label className="check text-xs">
                <input type="checkbox" checked={rawConfirm} onChange={event => setRawConfirm(event.target.checked)} />
                I know what I&apos;m doing
              </label>
              <button className="btn ghost sm" onClick={() => { setRawOpen(false); setRawConfirm(false) }}>Cancel</button>
              <button
                className="btn danger sm"
                disabled={saving || !rawConfirm}
                onClick={() => void handleRawSave()}
              >
                <Icon name="save" size={11} /> Overwrite
              </button>
            </div>
          </div>
        ) : (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: '26%' }}>Key</th>
                    <th style={{ width: '28%' }}>Value</th>
                    <th>Description</th>
                    <th style={{ width: 80 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {state?.keys.map(entry => {
                    const disabled = !canEdit || !entry.editable
                    const changed = values[entry.key] !== entry.value
                    return (
                      <tr key={entry.key}>
                        <td className="mono" style={{ fontWeight: 500, fontSize: 12, color: disabled ? 'var(--text-dim)' : 'var(--text)' }}>
                          {entry.key}
                          {!entry.editable && <span className="chip dim" style={{ marginLeft: 6 }}>read-only</span>}
                        </td>
                        <td>
                          <PropertyInput
                            entry={entry}
                            value={values[entry.key] ?? ''}
                            onChange={next => setValues(prev => ({ ...prev, [entry.key]: next }))}
                            disabled={disabled}
                          />
                        </td>
                        <td className="text-xs" style={{ opacity: 0.85 }}>{entry.description}</td>
                        <td>
                          {changed && <span className="chip warn">dirty</span>}
                          {entry.requiresRestart && <span className="chip dim" title="Requires restart" style={{ marginLeft: 4 }}>restart</span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DataState>
    </div>
  )
}
